import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { XMarkIcon, TrashIcon, PhotoIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import API_CONFIG from '../../config/api';

const BASE = `${API_CONFIG.baseURL}/api/template-preventivi`;
const clamp01 = (v) => Math.max(0, Math.min(1, v));

/**
 * Editor visuale per posizionare i campi su un template PDF (senza form field).
 * Le coordinate sono normalizzate (0..1), origine in alto a sinistra.
 */
export default function EditorCampiTemplate({ template, onClose }) {
  const templateId = template.id;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [catalogo, setCatalogo] = useState([]);
  const [pagina, setPagina] = useState({ numero: 1, larghezzaPt: 595, altezzaPt: 842 });
  const [campi, setCampi] = useState([]);
  const [imgUrl, setImgUrl] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [canvasW, setCanvasW] = useState(0);
  const [sostituendo, setSostituendo] = useState(false);

  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const fileInputRef = useRef(null);

  // Carica info editor + anteprima
  useEffect(() => {
    let objUrl = null;
    let annullato = false;
    (async () => {
      try {
        const { data } = await api.get(`${BASE}/${templateId}/editor`);
        if (annullato) return;
        if (data.success) {
          setCatalogo(data.catalogo || []);
          if (data.pagine && data.pagine.length) setPagina(data.pagine[0]);
          setCampi((data.campi || []).map((c, i) => ({
            tempId: `db-${c.id || i}`,
            chiaveCampo: c.chiaveCampo,
            tipo: c.tipo,
            pagina: c.pagina || 1,
            x: c.x, y: c.y,
            larghezza: c.larghezza,
            altezza: c.altezza,
            fontSize: c.fontSize,
            allineamento: c.allineamento,
            grassetto: !!c.grassetto,
            colore: c.colore || '#000000',
            mostraUnita: c.mostraUnita === undefined ? true : !!c.mostraUnita
          })));
        }
        const resp = await api.get(`${BASE}/${templateId}/anteprima?pagina=1`, { responseType: 'blob' });
        if (annullato) return;
        objUrl = URL.createObjectURL(resp.data);
        setImgUrl(objUrl);
      } catch (e) {
        toast.error('Errore caricamento editor: ' + (e.response?.data?.error || e.message));
      } finally {
        if (!annullato) setLoading(false);
      }
    })();
    return () => { annullato = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [templateId]);

  // Misura la larghezza del canvas per il preview del font
  const misuraCanvas = useCallback(() => {
    if (canvasRef.current) setCanvasW(canvasRef.current.getBoundingClientRect().width);
  }, []);
  useEffect(() => {
    misuraCanvas();
    window.addEventListener('resize', misuraCanvas);
    return () => window.removeEventListener('resize', misuraCanvas);
  }, [misuraCanvas, imgUrl]);

  const labelCampo = (chiave) => (catalogo.find(c => c.chiave === chiave)?.label) || chiave;
  const selected = campi.find(c => c.tempId === selectedId) || null;

  const aggiungiCampo = (cat) => {
    const nuovo = {
      tempId: `new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      chiaveCampo: cat.chiave,
      tipo: cat.tipo,
      pagina: 1,
      x: 0.35, y: 0.45,
      larghezza: cat.tipo === 'immagine' ? 0.25 : 0.35,
      altezza: cat.tipo === 'immagine' ? 0.18 : null,
      fontSize: 10, allineamento: 'left', grassetto: false, colore: '#000000',
      mostraUnita: true
    };
    setCampi(prev => [...prev, nuovo]);
    setSelectedId(nuovo.tempId);
  };

  const updateCampo = (tempId, patch) =>
    setCampi(prev => prev.map(c => (c.tempId === tempId ? { ...c, ...patch } : c)));
  const removeCampo = (tempId) => {
    setCampi(prev => prev.filter(c => c.tempId !== tempId));
    if (selectedId === tempId) setSelectedId(null);
  };

  // Drag / resize
  const onMouseDownBox = (e, campo, mode) => {
    e.preventDefault(); e.stopPropagation();
    setSelectedId(campo.tempId);
    const rect = canvasRef.current.getBoundingClientRect();
    dragRef.current = { tempId: campo.tempId, mode, startX: e.clientX, startY: e.clientY, orig: { ...campo }, rect };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };
  const onMouseMove = (e) => {
    const ds = dragRef.current;
    if (!ds) return;
    const dxN = (e.clientX - ds.startX) / ds.rect.width;
    const dyN = (e.clientY - ds.startY) / ds.rect.height;
    if (ds.mode === 'move') {
      updateCampo(ds.tempId, { x: clamp01(ds.orig.x + dxN), y: clamp01(ds.orig.y + dyN) });
    } else if (ds.mode === 'resizeW') {
      updateCampo(ds.tempId, { larghezza: Math.max(0.03, Math.min(1 - ds.orig.x, ds.orig.larghezza + dxN)) });
    } else if (ds.mode === 'resizeH') {
      updateCampo(ds.tempId, { altezza: Math.max(0.03, Math.min(1 - ds.orig.y, (ds.orig.altezza || 0.15) + dyN)) });
    }
  };
  const onMouseUp = () => {
    dragRef.current = null;
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
  };

  const onSostituisciPdf = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Seleziona un file PDF'); return; }
    setSostituendo(true);
    try {
      const fd = new FormData();
      fd.append('pdf', file);
      const { data } = await api.put(`${BASE}/${templateId}/sostituisci-pdf`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (data.success) {
        // Aggiorna le dimensioni pagina (il nuovo PDF potrebbe avere formato diverso)
        try {
          const info = await api.get(`${BASE}/${templateId}/editor`);
          if (info.data && info.data.pagine && info.data.pagine.length) setPagina(info.data.pagine[0]);
        } catch (er) { /* ignora */ }
        // Ricarica l'anteprima (cache-bust)
        const resp = await api.get(`${BASE}/${templateId}/anteprima?pagina=1&_=${Date.now()}`, { responseType: 'blob' });
        const nuovoUrl = URL.createObjectURL(resp.data);
        setImgUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return nuovoUrl; });
        toast.success('PDF sostituito. I campi posizionati sono stati mantenuti — verifica le posizioni e salva.');
      }
    } catch (err) {
      toast.error('Errore sostituzione PDF: ' + (err.response?.data?.error || err.message));
    } finally {
      setSostituendo(false);
    }
  };

  const salva = async () => {
    setSaving(true);
    try {
      const payload = campi.map(c => ({
        chiaveCampo: c.chiaveCampo, pagina: c.pagina || 1,
        x: c.x, y: c.y, larghezza: c.larghezza, altezza: c.altezza,
        fontSize: c.fontSize, allineamento: c.allineamento, grassetto: c.grassetto, colore: c.colore,
        mostraUnita: c.mostraUnita
      }));
      const { data } = await api.put(`${BASE}/${templateId}/campi`, { campi: payload });
      if (data.success) toast.success(`Layout salvato (${data.count} campi)`);
    } catch (e) {
      toast.error('Errore salvataggio: ' + (e.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  };

  const pxPerPt = canvasW && pagina.larghezzaPt ? canvasW / pagina.larghezzaPt : 1;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-2">
      <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-7xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
          <div>
            <h3 className="font-bold text-gray-900">Posiziona campi — {template.nomeTemplate}</h3>
            <p className="text-xs text-gray-500">Trascina i campi sul PDF. Le posizioni verranno usate per generare i preventivi.</p>
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileInputRef} type="file" accept="application/pdf" className="hidden" onChange={onSostituisciPdf} />
            <button onClick={() => fileInputRef.current && fileInputRef.current.click()} disabled={sostituendo}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 flex items-center gap-1">
              <ArrowUpTrayIcon className="h-4 w-4" />
              {sostituendo ? 'Caricamento…' : 'Sostituisci PDF'}
            </button>
            <button onClick={salva} disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Salvataggio…' : 'Salva layout'}
            </button>
            <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Palette campi */}
          <div className="w-56 border-r bg-gray-50 overflow-y-auto p-3 flex-shrink-0">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Campi disponibili</p>
            <div className="space-y-1">
              {catalogo.map(cat => (
                <button key={cat.chiave} onClick={() => aggiungiCampo(cat)}
                  className="w-full text-left text-xs px-2 py-1.5 rounded border border-gray-200 bg-white hover:border-blue-400 hover:bg-blue-50 flex items-center gap-1">
                  {cat.tipo === 'immagine' && <PhotoIcon className="h-3.5 w-3.5 text-gray-400" />}
                  <span className="truncate">{cat.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Canvas PDF */}
          <div className="flex-1 overflow-auto bg-gray-200 p-4 flex justify-center items-start">
            {loading ? (
              <div className="text-gray-500 mt-20">Caricamento anteprima…</div>
            ) : (
              <div ref={canvasRef} onMouseDown={() => setSelectedId(null)}
                className="relative bg-white shadow-lg select-none"
                style={{ width: '100%', maxWidth: 800 }}>
                {imgUrl && (
                  <img src={imgUrl} alt="Anteprima template" onLoad={misuraCanvas}
                    className="block w-full h-auto pointer-events-none" draggable={false} />
                )}
                {campi.map(campo => {
                  const isSel = campo.tempId === selectedId;
                  const style = {
                    left: `${campo.x * 100}%`,
                    top: `${campo.y * 100}%`,
                    width: `${campo.larghezza * 100}%`,
                  };
                  if (campo.tipo === 'immagine') {
                    return (
                      <div key={campo.tempId} style={{ ...style, height: `${(campo.altezza || 0.15) * 100}%` }}
                        onMouseDown={(e) => onMouseDownBox(e, campo, 'move')}
                        className={`absolute flex items-center justify-center text-[10px] text-gray-600 cursor-move border-2 ${isSel ? 'border-blue-500 bg-blue-500/10' : 'border-dashed border-gray-400 bg-gray-100/40'}`}>
                        <PhotoIcon className="h-5 w-5 text-gray-400 mr-1" /> Immagine vettura
                        <div onMouseDown={(e) => onMouseDownBox(e, campo, 'resizeW')}
                          className="absolute right-[-5px] top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-blue-500 rounded-full cursor-ew-resize" />
                        <div onMouseDown={(e) => onMouseDownBox(e, campo, 'resizeH')}
                          className="absolute bottom-[-5px] left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-blue-500 rounded-full cursor-ns-resize" />
                      </div>
                    );
                  }
                  const fontPx = Math.max(6, campo.fontSize * pxPerPt);
                  return (
                    <div key={campo.tempId} style={style}
                      onMouseDown={(e) => onMouseDownBox(e, campo, 'move')}
                      className={`absolute cursor-move border ${isSel ? 'border-blue-500 bg-blue-500/10' : 'border-dashed border-blue-300/70 hover:border-blue-400'}`}>
                      <div style={{
                        fontSize: `${fontPx}px`,
                        lineHeight: 1.2,
                        color: campo.colore,
                        fontWeight: campo.grassetto ? 700 : 400,
                        textAlign: campo.allineamento,
                        whiteSpace: 'normal',
                        overflow: 'hidden'
                      }}>
                        {labelCampo(campo.chiaveCampo)}
                      </div>
                      <div onMouseDown={(e) => onMouseDownBox(e, campo, 'resizeW')}
                        className="absolute right-[-5px] top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-blue-500 rounded-full cursor-ew-resize" />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Proprietà campo selezionato */}
          <div className="w-64 border-l bg-gray-50 overflow-y-auto p-3 flex-shrink-0">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Proprietà</p>
            {!selected ? (
              <p className="text-xs text-gray-400">Seleziona un campo per modificarne le proprietà, oppure clicca un campo a sinistra per aggiungerlo.</p>
            ) : (
              <div className="space-y-3">
                <div className="text-sm font-medium text-gray-800">{labelCampo(selected.chiaveCampo)}</div>

                {selected.tipo !== 'immagine' && (
                  <>
                    <label className="block text-xs text-gray-600">
                      Dimensione testo (pt): <span className="font-medium">{selected.fontSize}</span>
                      <input type="range" min="6" max="40" value={selected.fontSize}
                        onChange={(e) => updateCampo(selected.tempId, { fontSize: parseInt(e.target.value, 10) })}
                        className="w-full" />
                    </label>
                    <label className="block text-xs text-gray-600">
                      Allineamento
                      <select value={selected.allineamento}
                        onChange={(e) => updateCampo(selected.tempId, { allineamento: e.target.value })}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm mt-1">
                        <option value="left">Sinistra</option>
                        <option value="center">Centro</option>
                        <option value="right">Destra</option>
                      </select>
                    </label>
                    <label className="flex items-center gap-2 text-xs text-gray-600">
                      <input type="checkbox" checked={selected.grassetto}
                        onChange={(e) => updateCampo(selected.tempId, { grassetto: e.target.checked })} />
                      Grassetto
                    </label>
                    {catalogo.find(c => c.chiave === selected.chiaveCampo)?.unita && (
                      <label className="flex items-center gap-2 text-xs text-gray-600">
                        <input type="checkbox" checked={selected.mostraUnita !== false}
                          onChange={(e) => updateCampo(selected.tempId, { mostraUnita: e.target.checked })} />
                        Mostra unità ({catalogo.find(c => c.chiave === selected.chiaveCampo)?.unita})
                      </label>
                    )}
                    <label className="block text-xs text-gray-600">
                      Colore
                      <input type="color" value={selected.colore}
                        onChange={(e) => updateCampo(selected.tempId, { colore: e.target.value })}
                        className="w-full h-8 border border-gray-300 rounded mt-1" />
                    </label>
                  </>
                )}

                <div className="text-xs text-gray-500">
                  Larghezza box: {Math.round(selected.larghezza * 100)}%
                  {selected.tipo === 'immagine' && <> · Altezza: {Math.round((selected.altezza || 0.15) * 100)}%</>}
                </div>

                <button onClick={() => removeCampo(selected.tempId)}
                  className="w-full flex items-center justify-center gap-1 text-xs text-red-600 border border-red-200 rounded px-2 py-1.5 hover:bg-red-50">
                  <TrashIcon className="h-4 w-4" /> Rimuovi campo
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
